import { AnimatePresence, motion, useAnimation } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { ArrowLeftSvg } from '../../../assets/icons';
import ArrowRightSvg from '../../../assets/icons/ArrowRightSvg';
import theme from '../../../assets/theme';
import { useBreakPoint } from '../../../hooks/useBreakPoint';
import { ContentCategory } from '../../../utils/authenticatedHomeUtils';
import { Button } from '../../_atoms/Button';

const FilterButton = ({
  handleSelectCategory,
  index,
  icon,
  isActive,
  categoryId,
  categoryTitle,
}: {
  index: number;
  handleSelectCategory: (categoryId: string, key: number) => void;
  icon: React.ReactElement;
  categoryTitle: string;
  isActive: boolean;
  categoryId: string;
}) => {
  const animation = useAnimation();
  const hoverSequence = async () => {
    await animation.start({ scale: 1.03, transition: { duration: 0.1 } });
    animation.start({ scale: 1, transition: { duration: 0.1 } });
  };

  const isDesktop = useBreakPoint(800);

  return (
    <AnimatePresence>
      <motion.div
        animate={animation}
        onHoverStart={hoverSequence}
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'flex-end',
        }}
      >
        <StyledFilterButton
          key={index}
          $isActive={isActive}
          onClick={() => {
            handleSelectCategory(categoryId, index);
          }}
          icon={isDesktop ? <ButtonIconWrapper>{icon}</ButtonIconWrapper> : undefined}
        >
          {categoryTitle}
        </StyledFilterButton>
      </motion.div>
    </AnimatePresence>
  );
};

const ButtonIconWrapper = styled.div`
  position: absolute;
  top: -28px;
  right: calc(50% - 22.5px));
  display: flex;
  justify-content: center;
  align-items: center;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background-color: inherit;

  @media (max-width: 800px) {
  width: 25px;
  height: 25px;
  top: -15px;
  }
`;

export const CategoryList = ({
  visibleCategories,
  handleSelectCategory,
  activeCategoryId,
}: {
  handleSelectCategory: (categoryId: string) => void;
  visibleCategories: ContentCategory[];
  activeCategoryId?: string;
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [isLeftFilterArrowVisible, setShowLeftArrow] = useState(false);
  const [isRightFilterArrowVisible, setShowRightArrow] = useState(false);

  const handleScrollLeft = () => {
    const itemsToScroll = 2;

    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft - (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left: nextCheckpoint < 0 ? 0 : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };

  const handleScrollRight = () => {
    const itemsToScroll = 2;
    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft + (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left:
          nextCheckpoint > listRef.current.scrollWidth
            ? listRef.current.scrollWidth
            : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    // We have a scroll listener to always know the latest scroll position
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scrollHandler: EventListener = (ev: any) => {
      const scrollLeft = ev.target.scrollLeft;
      if (scrollLeft === 0) {
        setShowLeftArrow(false);
      } else {
        setShowLeftArrow(true);
      }

      if (scrollLeft + ev.target.clientWidth >= ev.target.scrollWidth - 15) {
        //10 as a buffer
        setShowRightArrow(false);
      } else {
        setShowRightArrow(true);
      }
    };
    const ref = listRef.current;
    ref?.addEventListener('scroll', scrollHandler);

    if (ref && ref.clientWidth < ref.scrollWidth) {
      // Upon mounting we check if we need to show the right arrow if there is a scroll
      setShowRightArrow(true);
    }

    return () => {
      if (ref) {
        ref.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [visibleCategories]);

  return (
    <FilterRow>
      {isLeftFilterArrowVisible && (
        <LeftArrowButtonContainer>
          <StyledArrowButton
            styles={{
              background: theme.colors.white,
              boxShadow: theme.effects.defaultShadow,
              border: 'none',
              height: 35,
              width: 35,
              borderRadius: '50%',
            }}
            type="ghost"
            icon={<ArrowLeftSvg />}
            notext={true}
            onClick={() => {
              handleScrollLeft();
            }}
          />
        </LeftArrowButtonContainer>
      )}

      {visibleCategories.length > 0 && (
        <FilterButtonsContainer ref={listRef}>
          {visibleCategories.map((category, i) => (
            <FilterButton
              key={i}
              index={i}
              categoryId={category.id}
              handleSelectCategory={handleSelectCategory}
              icon={category.icon}
              categoryTitle={category.title}
              isActive={activeCategoryId === category.id}
            />
          ))}
        </FilterButtonsContainer>
      )}
      {isRightFilterArrowVisible && (
        <RightButtonContainer>
          <StyledArrowButton
            styles={{
              background: theme.colors.white,
              boxShadow: theme.effects.defaultShadow,
              border: 'none',
              height: 35,
              width: 35,
              borderRadius: '50%',
            }}
            type="ghost"
            icon={<ArrowRightSvg />}
            notext={true}
            onClick={() => {
              handleScrollRight();
            }}
          />
        </RightButtonContainer>
      )}
    </FilterRow>
  );
};

const ArrowButtonContainer = styled.div`
  position: absolute;
  z-index: 2;
  bottom: 0px;
  width: 0px;
  height: 102px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  width: 50px;
  @media (max-width: 800px) {
    height: 56px;
  }
`;

const LeftArrowButtonContainer = styled(ArrowButtonContainer)`
  left: 0px;
  background: linear-gradient(to left, rgba(249, 151, 70, 0), rgba(255, 236, 221, 1));
`;
const RightButtonContainer = styled(ArrowButtonContainer)`
  right: 0px;
  background: linear-gradient(to right, rgba(249, 151, 70, 0), rgba(255, 236, 221, 1));
`;

const StyledArrowButton = styled(Button)`
  padding: 0px;
  svg {
    height: 28px;
    width: 28px;
  }
`;

const FilterRow = styled.div`
  position: relative;
  display: flex;
  gap: 10px;
  max-width: 100%;
  width: fit-content;
  align-items: end;
  align-self: start;

  @media (max-width: 800px) {
    height: 50px;
  }
`;

const FilterButtonsContainer = styled.div`
  display: grid;
  border-radius: 10px;
  grid-template-columns: repeat(auto-fill, max-content);
  grid-auto-rows: 100px;
  grid-auto-flow: column;
  align-items: end;
  gap: 10px;
  color: ${theme.colors.white};
  font-size: 16px;
  width: 100%;
  overflow-x: auto;
  padding: 3px 40px 3px 40px;
  scrollbar-width: none;
  transition: all 1s;
  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }

  @media (max-width: 800px) {
    grid-auto-rows: 50px;
    gap: 5px;
  }
`;

const StyledFilterButton = styled(Button)<{ $isActive: boolean }>`
  background-color: ${theme.colors.paleBeige};
  color: ${theme.colors.black};
  border: none;
  border-radius: 10px;
  font-weight: bold;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: calc(100% - 45px);
  flex-direction: column;
  width: 150px;
  padding: 8px 10px 5px 10px;
  margin-top: 30px;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  span {
    display: inline-block;
    word-wrap: break-word;
    white-space: pre-line;
    z-index: 1;
  }

  svg {
    width: 28px;
    height: 28px;
    margin: 0px;
  }

  &:hover {
    background-color: ${theme.colors.white};
    color: ${theme.colors.black};
  }

  &:focus {
    background-color: ${theme.colors.white};
    color: ${theme.colors.black};
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      padding: 20px 20px 20px 20px;
      height: calc(100% - 30px);
      width: 170px;
      background-color: ${theme.colors.white};
    `}

  @media (max-width: 800px) {
    width: 80px;
    padding: 8px 2px 5px 2px;
    border-radius: 5px;
    height: calc(100% - 15px);

    ${({ $isActive }) =>
      $isActive &&
      css`
        height: calc(100% - 10px);
      `}

    span {
      font-size: 10px;
      word-wrap: break-word;
      white-space: pre-line;
      line-height: 12px;
    }
    svg {
      width: 18px;
      height: 18px;
      margin: 0px;
    }
  }
`;
